import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Affix,
  AutoComplete,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PartialAddr } from 'domains/orgDomain';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import { useParentOrg } from './useParentOrg';

import states from 'states-us';
import { isUnitedStates } from 'utils/address';
import AddressValidation, {
  ValidationRef,
} from '../ShippingAdmin/components/AddressValidation';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { ArrowLeftIcon } from 'components/Icons';
import { Headline } from 'components/RevxTypography';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
const statesAbbrevs = states.map((x) => x.abbreviation);

const AddrItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 10 },
  };

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector((state) => state.loading.effects.org.saveAddr);
  const item = useAppSelector((state) => state.org.curAddr);

  const parentOrg = useParentOrg();
  const addrValidationRef = useRef<ValidationRef>(null);
  const doSave = async (values: PartialAddr) => {
    const updated = Object.assign({}, item, values);
    await dispatch.org.saveAddr({ orgId: parentOrg?.id || 0, addr: updated });
    onClose();
  };
  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    if (values.ignoreShippingValidation) {
      doSave(values);
      return;
    }
    const ret = await addrValidationRef.current?.validate({
      addressLine: values.addressLine1,
      city: values.city,
      stateProvinceCode: values.state,
      countryCode: values.country,
      postalCode: values.postalCode,
    });

    if (ret) {
      doSave(values);
    }
  };
  const onClose = () => {
    dispatch.org.setState({ curAddr: undefined });
  };
  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    const updated = Object.assign({}, item, values);
    updated.id = undefined;
    await dispatch.org.saveAddr({ orgId: parentOrg?.id || 0, addr: updated });
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.org.removeAddr({ orgId: parentOrg?.id || 0, addr: item });
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.org.addr.form.title.edit')
                : t('platform.org.addr.form.title.create')}
            </Headline>
          }
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave}>
              {t('common.save')}
            </RevxButton>,
            ...(item?.id
              ? [
                  <RevxButton onClick={onSaveAsNew} type="primary" brandGreen>
                    {t('common.saveAsNew')}
                  </RevxButton>,
                ]
              : []),
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(item?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  return (
    <RevxSpin spinning={saving}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          className="platform-item-form"
          initialValues={{
            ...item,
          }}
        >
          <AddressValidation
            address={{
              addressLine: form.getFieldValue('addressLine1'),
              city: form.getFieldValue('city'),
              stateProvinceCode: form.getFieldValue('state'),
              countryCode: form.getFieldValue('country'),
              postalCode: form.getFieldValue('postalCode'),
            }}
            onApply={(record) => {
              form.setFieldsValue({
                addressLine1: record.addressLine,
                city: record.city,
                state: record.stateProvinceCode,
                country: record.countryCode,
                postalCode: record.postalCode,
              });
            }}
            ref={addrValidationRef}
          />

          <Form.Item
            label={t('platform.org.addr.table.columns.addressLine1')}
            name={'addressLine1'}
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.addressLine2')}
            name={'addressLine2'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.city')}
            name="city"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.country !== cur.country}
          >
            {({ getFieldValue }) => (
              <Form.Item
                label={t('platform.org.addr.table.columns.state')}
                // only US addresses need a state; most countries have none (PRD-746).
                // `dependencies` re-validates when country changes, and the rule reads it as
                // it validates, so the error clears instead of sticking.
                dependencies={['country']}
                required={isUnitedStates(getFieldValue('country'))}
                rules={[
                  RULE_TEXT_LEN_255,
                  ({ getFieldValue: getCurrent }) => ({
                    required: isUnitedStates(getCurrent('country')),
                  }),
                ]}
                name="state"
              >
                {/* the list is US state codes, so offer it only for US addresses;
                    elsewhere a province is free text (PRD-746) */}
                {isUnitedStates(getFieldValue('country')) ? (
                  <AutoComplete>
                    {statesAbbrevs.map((s) => (
                      <AutoComplete.Option value={s} key={s}>
                        {s}
                      </AutoComplete.Option>
                    ))}
                  </AutoComplete>
                ) : (
                  <Input />
                )}
              </Form.Item>
            )}
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.country')}
            name="country"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.postalCode')}
            name="postalCode"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.phoneNumber')}
            name="phoneNumber"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.form.labels.ignoreShippingValidation')}
            name="ignoreShippingValidation"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default AddrItemForm;
